import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

/**
 * Universal Searchable Combobox Select Component
 * Zero inline CSS, accessible, keyboard-friendly with live search filter
 */
export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  required = false,
  className = '',
  disabled = false,
  icon: Icon
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearchTerm('');
    }
  }, [isOpen]);

  const filteredOptions = options.filter((opt) => {
    const label = typeof opt === 'string' ? opt : opt.label || opt.name || '';
    return label.toLowerCase().includes(searchTerm.toLowerCase().trim());
  });

  const selectedLabel = (() => {
    if (!value) return '';
    const found = options.find((opt) => {
      const val = typeof opt === 'string' ? opt : opt.value || opt.name || '';
      return val === value;
    });
    if (!found) return value;
    return typeof found === 'string' ? found : found.label || found.name || '';
  })();

  const handleSelect = (opt) => {
    const val = typeof opt === 'string' ? opt : opt.value || opt.name || '';
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative z-30 ${className}`}>
      {/* Hidden input for HTML form validation */}
      <input
        type="text"
        required={required}
        value={value}
        onChange={() => {}}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-2.5 py-2 text-xs border rounded-lg flex items-center justify-between text-left transition-all duration-200 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#00796b] ${
          isOpen ? 'border-[#00796b] ring-1 ring-[#00796b] bg-white' : 'border-gray-200 bg-gray-50/70 hover:bg-white'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={selectedLabel ? `${placeholder}: ${selectedLabel}` : placeholder}
      >
        <div className="flex items-center gap-1.5 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />}
          <span className={`truncate ${selectedLabel ? 'text-gray-800 font-semibold' : 'text-gray-600 font-medium'}`}>
            {selectedLabel || placeholder}
          </span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 text-[#00796b]' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-[100] left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in-50 zoom-in-95 duration-150 min-w-[240px]" role="listbox">
          {/* Search Box */}
          <div className="p-2 border-b border-gray-100 bg-gray-50/50">
            <div className="relative flex items-center">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:border-[#00796b] text-gray-800 placeholder:text-gray-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search input"
                  className="absolute right-2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="text-[10px] text-gray-400 mt-1 px-1 flex justify-between">
              <span>{filteredOptions.length} available</span>
              {searchTerm && <span>Filtering for &ldquo;{searchTerm}&rdquo;</span>}
            </div>
          </div>

          {/* Option List */}
          <div className="max-h-56 overflow-y-auto p-1 divide-y divide-gray-50 scrollbar-thin">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400">
                No matching locations or services found.
              </div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const val = typeof opt === 'string' ? opt : opt.value || opt.name || '';
                const label = typeof opt === 'string' ? opt : opt.label || opt.name || '';
                const desc = typeof opt === 'object' ? opt.desc : null;
                const isSelected = val === value;

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={`w-full px-2.5 py-2 text-left text-xs rounded-lg flex items-center justify-between transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-[#e6faf8] text-[#00796b] font-bold'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-[#00796b]'
                    }`}
                  >
                    <div className="truncate pr-2">
                      <div className="truncate leading-snug">{label}</div>
                      {desc && <div className="text-[10px] text-gray-400 truncate mt-0.5">{desc}</div>}
                    </div>
                    {isSelected && <Check className="w-3.5 h-3.5 text-[#00796b] shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
